import type { ComponentProps, ReactNode } from 'react'
import { cn } from '#lib/utils'

/**
 * The frame of a pictogram of the IO App Design System, drawn in the theme's
 * `--pictogram-*` tokens. Decorative unless `label` says what it stands for:
 * next to a title that already says it, it stays hidden from assistive technology.
 */
export function Pictogram({
	children,
	className,
	label,
	size = 120,
	...props
}: PictogramProps & { children: ReactNode }) {
	return (
		<svg
			aria-hidden={label ? undefined : true}
			aria-label={label}
			className={cn('shrink-0', className)}
			fill='none'
			height={size}
			role={label ? 'img' : undefined}
			viewBox='0 0 240 240'
			width={size}
			xmlns='http://www.w3.org/2000/svg'
			{...props}
		>
			{children}
		</svg>
	)
}

export type PictogramProps = Omit<ComponentProps<'svg'>, 'children'> & {
	/** The side in pixels: 72 beside text, 120 in an empty state, 180 on a page fallback. */
	size?: 72 | 120 | 180
	/** What the drawing stands for, when the text next to it does not say it. */
	label?: string
}
